// Figures (Primitives; was primitives.tsx's, HIERARCHY follow-up 9, BL-189): a title block's figures, at
// most five in a row: each its label, its value and a muted line under it, a rule above and below the
// strip and a hairline between them; two to a row on a phone, the values a size smaller (an odd one left
// over takes the row). A value never breaks across lines; its label may. A figure that stands for a page
// opens it (Home's "Owed to you"): a button drawn as the figure, its label underlined on hover. Figure
// is one on its own (a record's facts). A figure that's a problem has StateMark's red bar before it, its
// words saying why and staying ink (DESIGN.md §2: red is a mark, never text); KeyValue's figures layout,
// the Stock page's facts, folded in here (BL-189). They keep base.css's classes (.flw-figs, .flw-fig),
// which page sheets place by. size="sm" (KeyValue's figures, sm, folded in here, BL-189): up to six
// small facts inside a card, two to a row under a hairline, the figure at table size, a long label cut
// short (a technician's scorecard); across 4 puts four to a row (a section's counts), two on a phone.
// Reads only --figures-*; StateMark reads its own.
import type { ReactNode } from 'react';
import { StateMark } from '../StateMark/StateMark.tsx';
import './Figures.css';

export interface FigureProps {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  /** A problem: StateMark's red bar before the figure; the words stay ink. */
  bad?: boolean;
}

/** The figure, with the red bar before it when it's a problem. */
const figureOf = (value: ReactNode, bad: boolean | undefined) => (
  <b>
    {bad ? <StateMark state="problem" /> : null}
    {value}
  </b>
);

export function Figure({ label, value, sub, bad }: FigureProps) {
  return (
    <div className="flw-fig">
      <span>{label}</span>
      {figureOf(value, bad)}
      {sub != null && <small>{sub}</small>}
    </div>
  );
}

export interface FigureItem extends FigureProps {
  /** The page the figure stands for, if it opens one (Home's "Owed to you" opens who owes what). */
  onOpen?: () => void;
}

export interface FiguresProps {
  /** At most five (sm: six); any more aren't drawn. */
  items: readonly FigureItem[];
  /** Its name for screen readers, when nothing above it names it ("Sam's van today"). */
  label?: string;
  /** sm: up to six small facts inside a card, two to a row under a hairline, at table size. */
  size?: 'md' | 'sm';
  /** sm: how many to a row: 2 (a card's facts), or 4 (a section's counts); two on a phone. */
  across?: 2 | 4;
}

/** A title block's figures: at most five in a row, a rule above and below, hairlines between. */
export function Figures({ items, label, size = 'md', across = 2 }: FiguresProps) {
  const sm = size === 'sm';
  return (
    <div
      className="flw-figs"
      data-size={sm ? 'sm' : undefined}
      data-across={sm && across === 4 ? '4' : undefined}
      {...(label ? { role: 'group', 'aria-label': label } : {})}
    >
      {items.slice(0, sm ? 6 : 5).map((f) =>
        f.onOpen ? (
          <button key={f.label} type="button" className="flw-fig flw-fig-open" onClick={f.onOpen}>
            <span>{f.label}</span>
            {figureOf(f.value, f.bad)}
            {f.sub != null && <small>{f.sub}</small>}
          </button>
        ) : (
          <Figure key={f.label} label={f.label} value={f.value} sub={f.sub} bad={f.bad} />
        ),
      )}
    </div>
  );
}
